// ── Competition grounding UI ──────────────────────────────────
// Three pieces consumed by EvolutionTimeline / ForestSynthesis /
// NoveltyInspector:
//
//   <CompetitionBadge candidateId={...} />      — small badge for cards
//   <CompetitionTab />                            — full table view (NoveltyInspector tab)
//   <VerifyNoveltyPanel candidate={...} candidateId={...} />
//                                                — Tier 3 verify button + brief renderer
//
// Spec: features/ai-workflows/novelty-engine-phase-a.feature
//       (scenarios under "Competition grounding").

import React, { useState } from 'react';
import { useNovelty } from './NoveltyContext';

// ── Status → token color/icon ────────────────────────────────
function statusVisual(status) {
  switch (status) {
    case 'clear':   return { color: 'var(--success)',  icon: '✅', label: 'clear field' };
    case 'some':    return { color: 'var(--warning)',  icon: '🟡', label: 'some competition' };
    case 'crowded': return { color: 'var(--error)',    icon: '🔴', label: 'crowded space' };
    case 'unknown': return { color: 'var(--text-muted)', icon: '⚠️', label: 'check failed' };
    default:        return { color: 'var(--text-muted)', icon: '·',  label: 'not checked' };
  }
}

// ── 1) Compact badge — one line, click to expand competitor list ───
export function CompetitionBadge({ candidateId, compact = false }) {
  const { competition } = useNovelty();
  const [expanded, setExpanded] = useState(false);
  const data = competition?.[candidateId];
  if (!data) return null; // not yet scanned
  const v = statusVisual(data.status);
  const count = data.competitorCount ?? data.competitors?.length ?? 0;

  let summary;
  if (data.status === 'clear') summary = `${v.icon} ${v.label} · ${data.citations?.length || 0} sources checked`;
  else if (data.status === 'unknown') summary = `${v.icon} ${v.label}`;
  else summary = `${v.icon} ${count} competitor${count !== 1 ? 's' : ''} found`;

  return (
    <div
      role="region"
      aria-label={`Competition status: ${v.label}, ${count} competitors`}
      style={{
        marginTop: 4,
        padding: '4px 8px',
        borderRadius: 4,
        background: 'var(--bg-card)',
        border: `1px solid ${v.color}`,
        fontSize: compact ? 10 : 11,
        fontFamily: 'var(--font-mono)',
        color: 'var(--text-secondary)',
        cursor: count > 0 ? 'pointer' : 'default',
      }}
      onClick={() => count > 0 && setExpanded(!expanded)}
      title={data.gapStatement || v.label}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span style={{ color: v.color }}>{summary}</span>
        {count > 0 && (
          <span aria-hidden="true" style={{ marginLeft: 'auto', color: 'var(--text-muted)' }}>
            {expanded ? '▾' : '▸'}
          </span>
        )}
      </div>
      {expanded && data.competitors?.length > 0 && (
        <div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {data.competitors.slice(0, 4).map((c, i) => (
            <div key={i} style={{ fontSize: compact ? 10 : 11, color: 'var(--text-primary)', lineHeight: 1.4 }}>
              <strong>{c.name}</strong>
              {c.url && (
                <a
                  href={c.url} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  style={{ marginLeft: 6, color: 'var(--info)', fontSize: compact ? 9 : 10 }}
                >↗</a>
              )}
              <div style={{ color: 'var(--text-secondary)', fontSize: compact ? 10 : 11 }}>
                {c.what} · <em style={{ color: 'var(--text-muted)' }}>{c.distinguisher}</em>
              </div>
            </div>
          ))}
          {data.gapStatement && (
            <div style={{
              marginTop: 6, padding: '6px 8px', background: 'var(--bg-secondary)',
              borderRadius: 4, borderLeft: `3px solid ${v.color}`,
              fontSize: compact ? 10 : 11, color: 'var(--text-primary)',
            }}>
              <strong style={{ color: 'var(--text-muted)', fontSize: compact ? 9 : 10 }}>GAP:</strong> {data.gapStatement}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── 2) Tier 3: Verify button + deep brief renderer ───────────
export function VerifyNoveltyPanel({ candidate, candidateId }) {
  const { onVerifyNovelty, verifications } = useNovelty();
  const v = verifications?.[candidateId];
  const isLoading = v?._loading;
  const hasResult = v && !v._loading && (v.competitors || v.gapStatement);
  const error = v?._error;

  const verdictColor = (verdict) => {
    if (verdict === 'genuinely novel') return 'var(--success)';
    if (verdict === 'novel angle on existing space') return 'var(--info)';
    if (verdict === 'crowded space, weak differentiation') return 'var(--warning)';
    if (verdict === 'not novel') return 'var(--error)';
    return 'var(--text-muted)';
  };

  if (hasResult) {
    return (
      <div
        role="region"
        aria-label="Deep competitive brief"
        style={{
          marginTop: 12, padding: 12, borderRadius: 8,
          background: 'var(--bg-secondary)',
          border: `1px solid ${verdictColor(v.verdict)}`,
        }}
      >
        <div style={{ fontSize: 10, color: 'var(--text-muted)', letterSpacing: 1, marginBottom: 6, fontFamily: 'var(--font-mono)' }}>
          🔍 DEEP BRIEF · verdict:{' '}
          <span style={{ color: verdictColor(v.verdict), fontWeight: 700 }}>{v.verdict}</span>
          {' '}· confidence {v.confidence}/100
        </div>
        {v.competitors?.length > 0 && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
            {v.competitors.map((c, i) => (
              <div key={i} style={{ fontSize: 11, color: 'var(--text-primary)', lineHeight: 1.4 }}>
                <strong>{c.name}</strong>
                {c.url && <a href={c.url} target="_blank" rel="noopener noreferrer" style={{ marginLeft: 6, color: 'var(--info)', fontSize: 10 }}>↗</a>}
                {' · '}<span style={{ color: c.threatLevel === 'high' ? 'var(--error)' : c.threatLevel === 'low' ? 'var(--success)' : 'var(--warning)' }}>
                  {c.threatLevel} threat
                </span>
                <div style={{ color: 'var(--text-secondary)' }}>{c.oneLineDescription}</div>
              </div>
            ))}
          </div>
        )}
        {v.gapStatement && (
          <div style={{
            padding: 8, background: 'var(--bg-card)', borderRadius: 4,
            borderLeft: `3px solid ${verdictColor(v.verdict)}`,
            fontSize: 12, color: 'var(--text-primary)', lineHeight: 1.5,
          }}>
            <strong style={{ color: 'var(--text-muted)', fontSize: 10, fontFamily: 'var(--font-mono)' }}>GAP STATEMENT:</strong>
            <div style={{ marginTop: 4 }}>{v.gapStatement}</div>
          </div>
        )}
        {v.citations?.length > 0 && (
          <details style={{ marginTop: 8 }}>
            <summary style={{ fontSize: 10, color: 'var(--text-muted)', cursor: 'pointer', fontFamily: 'var(--font-mono)' }}>
              {v.citations.length} sources cited
            </summary>
            <ul style={{ margin: '6px 0 0 0', padding: '0 0 0 16px', fontSize: 10, color: 'var(--text-secondary)' }}>
              {v.citations.map((c, i) => (
                <li key={i}>
                  <a href={c.uri} target="_blank" rel="noopener noreferrer" style={{ color: 'var(--info)' }}>
                    {c.title || c.uri}
                  </a>
                </li>
              ))}
            </ul>
          </details>
        )}
        {onVerifyNovelty && (
          <button
            onClick={() => onVerifyNovelty(candidate, candidateId)}
            style={{
              marginTop: 8, background: 'transparent', color: 'var(--text-muted)',
              border: '1px solid var(--border)', padding: '4px 8px', fontSize: 10,
              borderRadius: 4, cursor: 'pointer', fontFamily: 'var(--font-mono)',
            }}
          >🔄 re-verify</button>
        )}
      </div>
    );
  }

  return (
    <div style={{ marginTop: 12 }}>
      <button
        onClick={() => onVerifyNovelty?.(candidate, candidateId)}
        disabled={isLoading || !onVerifyNovelty}
        aria-label="Verify novelty against the live web"
        style={{
          background: 'var(--bg-secondary)',
          color: isLoading ? 'var(--text-muted)' : 'var(--info)',
          border: '1px solid var(--info)',
          padding: '8px 12px',
          fontSize: 11,
          borderRadius: 4,
          cursor: isLoading ? 'wait' : 'pointer',
          fontFamily: 'var(--font-mono)',
          minHeight: 32,
        }}
      >
        {isLoading
          ? '🔍 Searching the web for real competitors…'
          : '🔍 Verify novelty against the live web'}
      </button>
      {error && (
        <div style={{ marginTop: 6, fontSize: 10, color: 'var(--error)', fontFamily: 'var(--font-mono)' }}>
          Verify failed: {error}
        </div>
      )}
    </div>
  );
}

// ── 3) NoveltyInspector tab — table view of all checked candidates ──
export function CompetitionTab() {
  const { competition, evolveGenerations, recombinations, blendScores } = useNovelty();
  const [filter, setFilter] = useState('all'); // 'all' | 'clear-only' | 'hide-crowded'

  // Build candidate label lookup from whatever's been generated
  const labels = {};
  Object.values(evolveGenerations || {}).flat().forEach(c => { if (c.id) labels[c.id] = c.label; });
  recombinations.forEach((r, i) => { labels[`blend-${i}`] = r.label; labels[`forest-blend-${i}`] = r.label; });
  Object.entries(blendScores || {}).forEach(([id]) => { if (!labels[id] && recombinations[parseInt(id.replace('blend-', ''), 10)]) labels[id] = recombinations[parseInt(id.replace('blend-', ''), 10)]?.label; });

  const rows = Object.entries(competition || {})
    .map(([candidateId, data]) => ({ candidateId, ...data, label: labels[candidateId] || candidateId }))
    .filter(r => {
      if (filter === 'clear-only') return r.status === 'clear';
      if (filter === 'hide-crowded') return r.status !== 'crowded';
      return true;
    })
    .sort((a, b) => {
      // Clear first, then some, then crowded, then unknown
      const order = { clear: 0, some: 1, crowded: 2, unknown: 3 };
      return (order[a.status] ?? 4) - (order[b.status] ?? 4);
    });

  if (Object.keys(competition || {}).length === 0) {
    return (
      <div style={{ padding: 20, fontSize: 12, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)' }}>
        No competition data yet. The engine auto-scans the top candidates against the live web after Phase B completes.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      {/* Filter chips */}
      <div style={{ display: 'flex', gap: 6, fontSize: 10, fontFamily: 'var(--font-mono)' }}>
        {[
          { id: 'all', label: 'all' },
          { id: 'clear-only', label: '✅ clear field only' },
          { id: 'hide-crowded', label: '🌐 hide crowded' },
        ].map(f => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            aria-pressed={filter === f.id}
            style={{
              background: filter === f.id ? 'var(--bg-card-elevated)' : 'transparent',
              color: filter === f.id ? 'var(--info)' : 'var(--text-muted)',
              border: `1px solid ${filter === f.id ? 'var(--info)' : 'var(--border)'}`,
              padding: '3px 8px', borderRadius: 12, cursor: 'pointer',
              fontFamily: 'var(--font-mono)',
            }}
          >{f.label}</button>
        ))}
        <span style={{ marginLeft: 'auto', alignSelf: 'center', color: 'var(--text-muted)' }}>
          {rows.length} of {Object.keys(competition || {}).length} candidate{rows.length !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Rows */}
      <div role="list" style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {rows.map(row => {
          const v = statusVisual(row.status);
          const count = row.competitorCount ?? row.competitors?.length ?? 0;
          return (
            <div
              key={row.candidateId}
              role="listitem"
              style={{
                background: 'var(--bg-card)',
                borderLeft: `4px solid ${v.color}`,
                border: '1px solid var(--border)',
                borderLeftWidth: 4,
                borderRadius: 4,
                padding: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 4 }}>
                <span aria-hidden="true">{v.icon}</span>
                <span style={{ fontSize: 12, color: 'var(--text-primary)', fontWeight: 600, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {row.label}
                </span>
                <span style={{ fontSize: 10, color: v.color, fontFamily: 'var(--font-mono)' }}>
                  {row.status === 'clear' ? `${row.citations?.length || 0} sources` : `${count} competitor${count !== 1 ? 's' : ''}`}
                </span>
              </div>
              {row.competitors?.length > 0 && (
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)' }}>
                  {row.competitors.slice(0, 3).map(c => c.name).join(' · ')}
                  {row.competitors.length > 3 && ` +${row.competitors.length - 3} more`}
                </div>
              )}
              {row.gapStatement && row.status === 'clear' && (
                <div style={{ fontSize: 11, color: 'var(--success)', marginTop: 4, fontStyle: 'italic' }}>
                  {row.gapStatement}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default { CompetitionBadge, VerifyNoveltyPanel, CompetitionTab };
